// src/utils/permissions.js
//
// Modelo de roles (ROLES_PLAN.md). Un usuario tiene exactamente UN rol.
//   super_admin   — equipo interno, todo.
//   pa_admin      — administrador de una compañía de P.A: sus reclamos, los de
//                   sus P.A, y crea P.A adicionales.
//   pa            — Public Adjuster: solo sus reclamos; crea sus asistentes.
//   pa_assistant  — ve lo mismo que su P.A, firma las acciones con su cuenta.
//   csr_assistant — personal CSR por compañías asignadas; ve el 100 % del
//                   cheque, nunca lo que gana el P.A ni CSR.
//
// Los datos viejos no traen `role`: se infiere (viewAllClaims → pa_admin,
// adjusters.role === 'assistant' → pa_assistant, el resto → pa), así la
// migración no bloquea a nadie. La UI decide con can(); la seguridad real
// está en firestore.rules, que debe imponer lo mismo.

export const ROLES = ['super_admin', 'pa_admin', 'pa', 'pa_assistant', 'csr_assistant'];

export const ROLE_LABELS = {
  super_admin: { es: 'Super administrador', en: 'Super admin' },
  pa_admin: { es: 'P.A Admin', en: 'P.A Admin' },
  pa: { es: 'P.A', en: 'P.A' },
  pa_assistant: { es: 'Asistente de P.A', en: 'P.A Assistant' },
  csr_assistant: { es: 'CSR Assistant', en: 'CSR Assistant' }
};

const CAPABILITIES = {
  // Pantallas / menú
  viewOverview: ['super_admin', 'pa_admin'],
  viewCheckCommissions: ['super_admin', 'pa_admin', 'pa', 'pa_assistant'], // la pestaña; el monto de CSR dentro de ella es viewEarnings
  viewAllCompanies: ['super_admin'],
  // Dinero
  viewEarnings: ['super_admin', 'pa_admin'],          // ganancias y deuda con CSR (pa_admin: solo su compañía)
  // Lo que gana el P.A con SUS cheques: honorarios − comisión de CSR, antes del
  // reparto con su P.A Admin (que el sistema no conoce). Sin deuda con CSR:
  // eso solo lo ve el P.A Admin (viewEarnings). El asistente ve lo de su P.A.
  viewOwnEarnings: ['pa', 'pa_assistant'],
  viewBreakdown: ['super_admin', 'pa_admin', 'pa', 'pa_assistant'],
  uploadPayments: ['super_admin'],                     // comprobantes Zelle/BofA recibidos
  downloadInvoice: ['super_admin'],
  // Cortes de pagos (Facturas y deuda → Cortes): el P.A Admin solo los de su compañía.
  viewPaymentCuts: ['super_admin', 'pa_admin'],
  handleCsrPaymentSteps: ['super_admin'],              // csr_payment, % de collect, archivado
  // Reclamos
  createClaimForOthers: ['super_admin', 'pa_admin', 'csr_assistant'],
  moveCsrSteps: ['super_admin', 'csr_assistant'],
  movePaSteps: ['super_admin', 'pa_admin', 'pa', 'pa_assistant', 'csr_assistant'],
  // Avisos de firma de DocuSign: los asigna a su reclamo (el Super Admin lo hace desde Casos y notas)
  assignDocusign: ['super_admin', 'csr_assistant'],
  // Usuarios
  manageUsers: ['super_admin', 'pa_admin', 'pa']
};

// Quién puede crear a quién (ROLES_PLAN.md §9).
const CREATABLE_ROLES = {
  super_admin: ['pa_admin', 'pa', 'pa_assistant', 'csr_assistant'],
  pa_admin: ['pa'],
  pa: ['pa_assistant'],
  pa_assistant: [],
  csr_assistant: []
};

export const isRole = role => ROLES.includes(role);

export const can = (role, capability) => (CAPABILITIES[capability] || []).includes(role);

export const creatableRoles = role => CREATABLE_ROLES[role] || [];

export const canCreateRole = (actorRole, targetRole) => creatableRoles(actorRole).includes(targetRole);

// Rol de un puntero `adjusterAccess` (o del perfil en `adjusters`), tolerando
// documentos anteriores a la reestructuración.
export const resolveAdjusterRole = (access = {}, profile = {}) => {
  if (isRole(access.role) && access.role !== 'super_admin') return access.role;
  if (profile.role === 'csr_assistant') return 'csr_assistant';
  if (profile.role === 'assistant') return 'pa_assistant';
  if (access.viewAllClaims === true) return 'pa_admin';
  return 'pa';
};

// Compañías a las que llega un usuario: csr_assistant puede tener varias.
export const companiesOf = (access = {}) => {
  if (Array.isArray(access.companies) && access.companies.length) return access.companies.filter(Boolean);
  return access.company ? [access.company] : [];
};
